import type { SourceConnectionCode } from "@/models/sourceConnection";
import type { SourceConnectionFailureCode } from "@/lib/toolCardSchemas/connectSource";

const LEGACY_CALLBACK_ERROR_MAP: Record<string, SourceConnectionFailureCode> = {
  access_denied: "OAUTH_ACCESS_DENIED",
  insufficient_scope: "OAUTH_INSUFFICIENT_SCOPE",
  timeout: "OAUTH_TIMEOUT",
  login_required: "OAUTH_LOGIN_REQUIRED",
};

const SAFE_MESSAGES: Record<SourceConnectionFailureCode | SourceConnectionCode, string> = {
  PLUGIN_CONNECTED: "{Provider} is connected and available for this project.",
  PLUGIN_SKIPPED: "Plugin connection was skipped.",
  PLUGIN_DISMISSED: "{Provider} suggestions are muted for this chat.",
  POPUP_BLOCKED:
    "The browser blocked the secure provider window. Allow popups and try again.",
  POPUP_CLOSED: "The provider window closed before the connection was confirmed.",
  OAUTH_TIMEOUT: "The connection was not confirmed in time. Try again.",
  OAUTH_LOGIN_REQUIRED: "Sign in to SwiftCNS again, then retry the connection.",
  OAUTH_ACCESS_DENIED: "Provider access was not approved.",
  OAUTH_INSUFFICIENT_SCOPE:
    "The provider did not grant the permissions this plugin requires.",
  OAUTH_STATE_INVALID:
    "The authorization response could not be verified. Start a new connection attempt.",
  OAUTH_CALLBACK_INVALID:
    "The authorization response could not be verified. Start a new connection attempt.",
  OAUTH_AUTHORIZATION_ERROR: "The provider could not complete authorization. Try again.",
  OAUTH_TOKEN_EXCHANGE_FAILED: "The provider could not complete authorization. Try again.",
  OAUTH_UNKNOWN_REGISTRATION:
    "This plugin's authorization setup is unavailable. Contact support.",
  PLUGIN_PREPARE_FAILED: "SwiftCNS could not prepare the plugin connection. Try again.",
  PLUGIN_ENABLEMENT_FAILED:
    "SwiftCNS could not enable the plugin for this project. Try again.",
  PLUGIN_VERIFICATION_FAILED:
    "Authorization returned, but the plugin connection could not be verified.",
};

export class SourceOAuthError extends Error {
  readonly code: SourceConnectionFailureCode;
  readonly credentialPublicId?: string;

  constructor(code: SourceConnectionFailureCode, credentialPublicId?: string) {
    super(sourceConnectionSafeMessage(code, "Plugin"));
    this.name = "SourceOAuthError";
    this.code = code;
    this.credentialPublicId = credentialPublicId;
  }
}

export function isSourceOAuthError(error: unknown): error is SourceOAuthError {
  return error instanceof SourceOAuthError;
}

/**
 * Normalizes callback query parameters and legacy error aliases to a stable failure code.
 */
export function normalizeOAuthCallbackErrorCode(
  errorCode?: string | null,
  legacyError?: string | null,
): SourceConnectionFailureCode | null {
  const raw = errorCode ?? legacyError;
  if (!raw) return null;
  if (raw in SAFE_MESSAGES) {
    return raw as SourceConnectionFailureCode;
  }
  return LEGACY_CALLBACK_ERROR_MAP[raw] ?? null;
}

export function sourceConnectionSafeMessage(
  code: SourceConnectionFailureCode | SourceConnectionCode | string,
  providerDisplayName: string,
): string {
  const template =
    SAFE_MESSAGES[code as keyof typeof SAFE_MESSAGES] ??
    "The plugin connection could not be completed. Try again.";
  return template.replaceAll("{Provider}", providerDisplayName || "Plugin");
}
